// ============================================================
// RoleContext.js
// Resolves the signed-in user's role from Firestore, falling back to the file.
//
// ROLES NOW RESTRICT ACCESS.
//
//   'admin' — the office. Every route, exactly as before.
//   'shop'  — a workshop tablet. One screen (/shop), no sidebar, and every
//             other route redirects back to it. See the guard in App.js.
//
// The lookup ran for a full step with everyone resolving to 'admin', so this
// change is the only thing that can alter who sees what.
//
// ROLES COME FROM FIRESTORE (`teamRoles`), WITH THE FILE AS A FALLBACK.
//
// Resolution order — the lockout guard, and the reason this is worth reading:
//   1. BREAK GLASS. sales@ is admin before any lookup, database or file.
//   2. The Firestore record, which is what Settings writes.
//   3. data/teamMembers.js, whenever Firestore cannot answer — the read FAILED,
//      the collection is empty, or it holds nothing for this address.
//   4. The restricted role, for an address genuinely unknown to both, with a
//      console warning that says WHICH of the two cases it was.
//
// A FAILED READ THEREFORE LANDS AT STEP 3, NEVER AT STEP 4 BECAUSE OF THE
// FAILURE. The five accounts in the file are unaffected by any Firestore
// problem whatsoever.
//
// TIMING — no new loading state. The file answers SYNCHRONOUSLY the moment auth
// reports in, so the first paint is exactly what it is today; the Firestore read
// then upgrades the role if it says something different. `loading` stays false,
// so no page's loading branch behaves differently. The one consequence, worth
// knowing: someone who exists ONLY in Firestore renders as the restricted role
// for the moment the read is in flight.
//
// The `users` collection is still untouched: the original implementation that
// read (and seeded) it stays commented out at the bottom of this file, and
// `teamRoles` is a different, new collection. See utils/teamRolesDb.js.
// ============================================================

import React, { createContext, useState, useEffect, useMemo } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import {
  resolveRoleFor, resolveCreditHoldFor, mentionableNamesFrom, mergeTeamRecords,
} from '../data/teamMembers';
import { fetchTeamRoles } from '../utils/teamRolesDb';

/** Resolve, and say out loud when nobody has granted this address anything. */
function resolveWithWarning(email, state) {
  const { role, source } = resolveRoleFor(email, state);
  // Nothing is known about Firestore yet (first paint, read in flight). The
  // file's answer stands for now and the effect will re-resolve, so warning
  // here would cry wolf about every person who lives only in the database.
  const notReadYet = state?.records === null && state?.readOk === null;
  if (notReadYet) return role;
  if (source === 'unknown') {
    console.warn(
      '[RoleContext] "' + email + '" is in neither the teamRoles collection nor ' +
      'src/data/teamMembers.js — resolving as "' + role + '". Add them in Settings.'
    );
  } else if (source === 'unknown-read-failed') {
    console.warn(
      '[RoleContext] The teamRoles read FAILED and "' + email + '" is not in ' +
      'src/data/teamMembers.js either — resolving as "' + role + '". This is a ' +
      'failed read, not a missing person: check the connection before changing anything.'
    );
  }
  return role;
}

export const RoleContext = createContext(null);

export function RoleProvider({ children }) {
  const [userName, setUserName] = useState('');
  const [role, setRole] = useState('admin');
  // The signed-in address itself, for capabilities that name people rather than
  // roles (see canManageCreditHold below). userName cannot be used for that: it
  // is the email PREFIX, so it says nothing about the domain.
  const [userEmail, setUserEmail] = useState('');
  // The Firestore side. `records` null means "not read yet"; readOk false means
  // the read failed. Both resolve through the file, which is the point.
  const [teamState, setTeamState] = useState({ records: null, readOk: null });
  // Bumped by Settings after it writes, so the signed-in user's own role and the
  // mention list refresh without a reload.
  const [teamVersion, setTeamVersion] = useState(0);

  useEffect(() => {
    let live = true;
    (async () => {
      const { ok, records } = await fetchTeamRoles();
      if (live) setTeamState({ records, readOk: ok });
    })();
    return () => { live = false; };
  }, [teamVersion]);

  // The role is re-derived whenever auth or the Firestore list changes: the file
  // answers immediately, the database upgrades it when it lands.
  useEffect(() => {
    if (userEmail) setRole(resolveWithWarning(userEmail, teamState));
  }, [userEmail, teamState]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser?.email) {
        // ⚠️ userName is DERIVED FROM THE EMAIL PREFIX and must stay that way.
        //
        // This string is not a display convenience — it is the app's identity
        // for a person. Every saved document stores it as salesperson, every
        // board comment as author, every notification as recipient, and both
        // Documents.js and Dashboard.js filter those with ===. Switching it to
        // a name field from teamMembers, or from anywhere else, would silently
        // orphan every record already written under the old string: a
        // salesperson would stop seeing their own documents.
        //
        // teamMembers.js derives its display names by this same rule, so the
        // two agree — but the source here stays the live session's email.
        setUserName(firebaseUser.email.split('@')[0].toUpperCase());
        setUserEmail(firebaseUser.email);
        // Synchronously, from the FILE — deliberately not from teamState, which
        // this callback would only ever see a stale copy of. This is the first
        // paint's role, identical to today's behaviour for every account in the
        // file; the effect above re-resolves against Firestore the moment the
        // read lands, and again whenever it changes.
        setRole(resolveWithWarning(firebaseUser.email, { records: null, readOk: null }));
      } else {
        // Signed out. 'admin' keeps the pre-login render identical to what it
        // has always been; App.js shows the Login route regardless of role.
        setUserName('');
        setUserEmail('');
        setRole('admin');
      }
    });
    return unsubscribe;
  }, []);

  // Derived once per read, not per consumer.
  const teamRecords = useMemo(() => mergeTeamRecords(teamState.records), [teamState]);
  const mentionableNames = useMemo(() => mentionableNamesFrom(teamState.records), [teamState]);

  const value = {
    role,
    isAdmin: role === 'admin',
    isSalesperson: role === 'salesperson',
    // A shop user is NOT an admin — every isAdmin branch in the app already
    // reads false for them, which is what keeps the office UI out of their way
    // even before the route guard runs.
    isShop: role === 'shop',
    userName,
    userEmail,
    // The effective team — the file merged with Firestore — for Settings, and
    // the mention names derived from it. MERGED, never replaced: a failed or
    // empty read still offers the five known people rather than an empty list.
    teamRecords,
    mentionableNames,
    // True when the Firestore read failed, so Settings can say so rather than
    // showing the file's five as though they were the whole picture.
    teamReadFailed: teamState.readOk === false,
    /** Re-read teamRoles — called by Settings after it writes. */
    refreshTeam: () => setTeamVersion(v => v + 1),
    // A CAPABILITY, not a role: two of the five admins may place or lift a
    // credit hold; everyone else sees it read-only. UI-level only — see the
    // note in teamMembers.js. False while signed out, so nothing renders as
    // editable before auth reports in.
    canManageCreditHold: resolveCreditHoldFor(userEmail, teamState),
    // Still null: nothing reads a Firestore user record, and inventing one here
    // would imply a lookup that does not happen.
    userRecord: null,
    // Resolution is synchronous once auth reports in, and role starts at
    // 'admin', so there is no window in which a page sees a reduced role while
    // waiting. Kept false so no page's loading branch behaves differently.
    loading: false,
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   ORIGINAL ROLE-BASED IMPLEMENTATION — uncomment to restore.

import { collection, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

const SEED_USERS = [
  { email: 'ana@atlanticfence.com',    name: 'ANA',    role: 'admin' },
  { email: 'steve@atlanticfence.com',  name: 'STEVE',  role: 'admin' },
  { email: 'brett@atlanticfence.com',  name: 'BRETT',  role: 'admin' },
  { email: 'jordan@atlanticfence.com', name: 'JORDAN', role: 'admin' },
  { email: 'logan@atlanticfence.com',  name: 'LOGAN',  role: 'salesperson' },
];

export function RoleProvider({ children }) {
  const [role, setRole] = useState(null);
  const [userRecord, setUserRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setRole(null);
        setUserRecord(null);
        setLoading(false);
        return;
      }

      try {
        let snap = await getDocs(collection(db, 'users'));
        if (snap.empty) {
          await Promise.all(
            SEED_USERS.map(u => addDoc(collection(db, 'users'), { ...u, createdAt: new Date().toISOString() }))
          );
          snap = await getDocs(collection(db, 'users'));
        }

        const allUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const authEmail = (firebaseUser.email || '').toLowerCase();
        const matched = allUsers.find(u => (u.email || '').toLowerCase() === authEmail);

        if (matched) {
          setUserRecord(matched);
          setRole(matched.role);
        } else {
          const fallback = {
            email: firebaseUser.email,
            name: firebaseUser.email.split('@')[0].toUpperCase(),
            role: 'salesperson',
          };
          setUserRecord(fallback);
          setRole('salesperson');
        }
      } catch (err) {
        console.error('Error loading role:', err);
        setRole('salesperson');
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const isAdmin = role === 'admin';
  const isSalesperson = role === 'salesperson';
  const userName = userRecord?.name || '';

  return (
    <RoleContext.Provider value={{ role, isAdmin, isSalesperson, userName, userRecord, loading }}>
      {children}
    </RoleContext.Provider>
  );
}
─────────────────────────────────────────────────────────────────────────── */
